import React from 'react';

import RACES from '../../data/races';
import STATS from '../../data/stats';
import SKILLS from '../../data/skills';
import STAR_SIGNS from '../../data/star_signs';

import { getAllSkills, getStatColor } from '../../utils/helpers';
import { btnStyle } from '../../styles/theme';

import SectionHeader from '../ui/SectionHeader';
import ReviewSection from '../ui/ReviewSection';



// ─── STEP 5: REVIEW ─────────────────────────────────────────────────────────
function ReviewStep({ character, finalStats, onSave }) {
  const race = RACES.find(r => r.id === character.race);
  const starSign = STAR_SIGNS.find(s => s.id === character.starSign);
  const allSkills = getAllSkills();

  // Gather all perks
  const perks = [
    ...character.selectedPerks.map(i => race.perks[i]),
    ...(character.freePerk ? [allSkills.find(s => s.id === character.freePerk)?.name] : [])
  ].filter(Boolean);

  // Gather allocated skills
  const allocatedSkills = Object.entries(character.skills).filter(([, lvl]) => lvl > 0).map(([id, lvl]) => {
    const skill = allSkills.find(s => s.id === id);
    return { name: skill?.name || id, level: lvl };
  });

  // Modifiers
  const modifiers = {
    might: Math.floor(finalStats.might / 10),
    speed: Math.floor(finalStats.speed / 10),
    intellect: Math.floor(finalStats.intellect / 10),
  };
  const magicPower = Math.floor(finalStats.magic / 5);

  function handleSave() {
    // Convert character creation format to leveling format for saving
    const levelingChar = {
      name: character.name,
      race: character.race,
      stats: finalStats,
      skills: character.skills,
      magics: {},
      perks: perks,
      starSign: character.starSign,
      alchemy: { auxiliary: 1, organic: 1, conversion: 1, augmentation: 1 },
      weapons: [],
      armor: { head: "none", arms: "none", body: "none", legs: "none" },
      shield: "none",
      equipment: [],
      gear: [],
    };

    const expPools = { universal: 0, might: 0, speed: 0, intellect: 0 };

    if (onSave && onSave(levelingChar, expPools)) {
      alert('Character saved successfully!');
    }
  }

  return (
    <div>
      <SectionHeader style={{ textAlign: "center" }}>Character Sheet</SectionHeader>
      {/* Name & Race Banner */}
      <div style={{ textAlign: "center", background: "linear-gradient(180deg, rgba(45,35,20,0.6), rgba(20,16,10,0.4))", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "10px", padding: "20px", marginBottom: 20 }}>
        <div style={{ fontFamily: "'Cinzel Decorative', cursive", fontSize: "clamp(22px, 5vw, 32px)", color: "#c9a84c", textShadow: "0 0 12px rgba(201,168,76,0.3)" }}>{character.name}</div>
        <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#8a7e5e", marginTop: 4 }}>{race.name} · Movement: {race.movement} Steps</div>
        {onSave && (
          <button onClick={handleSave} style={{ ...btnStyle("#16a085", "rgba(22,160,133,0.15)"), padding: "8px 24px", marginTop: 12 }}>
            💾 Save Character
          </button>
        )}
      </div>

      {/* Stats Block */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: 20 }}>
        {STATS.map(stat => {
          const mod = stat.id !== "magic" ? modifiers[stat.id] : null;
          return (
            <div key={stat.id} style={{ background: "rgba(20,16,10,0.6)", border: `1px solid ${stat.color}44`, borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
              <div style={{ fontSize: "9px", color: stat.color, fontFamily: "'Cinzel', serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{stat.label}</div>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: "28px", color: stat.color }}>{finalStats[stat.id]}</div>
              {mod !== null && <div style={{ fontSize: "10px", color: "#6b6048" }}>Modifier: +{mod}</div>}
              {stat.id === "magic" && <div style={{ fontSize: "10px", color: "#6b6048" }}>Power: {magicPower} dice</div>}
            </div>
          );
        })}
      </div>

      {/* Two-column layout: Perks/Skills left, Abilities/Star Sign right */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {/* Left */}
        <div>
          <ReviewSection title="Perks" color="#c9a84c">
            {perks.map((p, i) => <div key={i} style={{ fontSize: "13px", color: "#a08d4e", padding: "3px 0", borderBottom: "1px solid rgba(201,168,76,0.08)" }}>+2 {p}</div>)}
          </ReviewSection>
          <ReviewSection title="Skills" color="#c9a84c" style={{ marginTop: 12 }}>
            {allocatedSkills.length === 0
              ? <div style={{ fontSize: "12px", color: "#5a5040" }}>None allocated</div>
              : allocatedSkills.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#a08d4e", padding: "3px 0", borderBottom: "1px solid rgba(201,168,76,0.08)" }}>
                  <span>{s.name}</span>
                  <span style={{ color: "#c9a84c" }}>Lvl {s.level}</span>
                </div>
              ))}
          </ReviewSection>
        </div>
        {/* Right */}
        <div>
          <ReviewSection title="Race Abilities" color="#5dade2">
            {race.abilities.map((a, i) => (
              <div key={i} style={{ padding: "5px 0", borderBottom: i < race.abilities.length - 1 ? "1px solid rgba(93,173,226,0.1)" : "none" }}>
                <div style={{ fontSize: "13px", color: "#5dade2aa" }}>{a.name}</div>
                <div style={{ fontSize: "11px", color: "#6b6048", marginTop: 2, lineHeight: 1.4 }}>{a.desc}</div>
              </div>
            ))}
          </ReviewSection>
          {starSign && (
            <ReviewSection title="Star Sign" color="#c9a84c" style={{ marginTop: 12 }}>
              <div style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#c9a84c" }}>{starSign.name} — <span style={{ fontStyle: "italic", color: "#8a7e5e" }}>"{starSign.title}"</span></div>
              <div style={{ fontSize: "12px", color: "#a09070", marginTop: 4, lineHeight: 1.5 }}>{starSign.effect}</div>
            </ReviewSection>
          )}
          <ReviewSection title="Alchemy" color="#8e44ad" style={{ marginTop: 12 }}>
            <div style={{ fontSize: "13px", color: "#c39bd3" }}>Starts at Class 1 (all subsets)</div>
            <div style={{ fontSize: "11px", color: "#6b6048", marginTop: 2 }}>Auxiliary · Organic · Conversion · Augmentation</div>
          </ReviewSection>
        </div>
      </div>
    </div>
  );
}

export default ReviewStep
